import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useRvp } from "@/context/RvpContext";
import ReversePickupDialog from "@/components/ReversePickupDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Undo2, Plus, Loader2, Search, MoreVertical, ExternalLink, X, ArrowRight } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }); } catch { return "—"; }
}

export default function ReversePickup() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const pendingRows = useMemo(() => {
    const q = pendingSearch.trim().toLowerCase();
    if (!q) return pending;
    return pending.filter((p) => [p.order_id, p.tracking_number, p.rvp_order_id, p.city, p.state, p.rvp_reason]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [pending, pendingSearch]);
  const [addOpen, setAddOpen] = useState(false);
  const [flagFor, setFlagFor] = useState(null);
  const { refresh: refreshCount } = useRvp();
  const navigate = useNavigate();

  const load = useCallback((t) => {
    setLoading(true);
    const target = t === "scheduled" ? "scheduled" : "pending";
    api.get(`/reverse-pickups?tab=${target}`)
      .then((r) => {
        const data = Array.isArray(r.data) ? r.data : [];
        if (target === "pending") setPending(data); else setScheduled(data);
      })
      .catch(() => { if (target === "pending") setPending([]); else setScheduled([]); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);
  // keep pending count fresh for the tab badge
  useEffect(() => { api.get("/reverse-pickups?tab=pending").then((r) => setPending(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scheduled;
    return scheduled.filter((s) =>
      [s.order_id, s.rvp_order_id, s.rvp_tracking_number, s.rvp_courier_partner, s.rvp_reason]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [scheduled, search]);

  const removeFlag = async (s) => {
    if (!window.confirm(`Remove #${s.order_id} from reverse pickup?`)) return;
    try {
      await api.post(`/shipments/${s.id}/reverse-pickup/remove`);
      toast.success("Removed from reverse pickup");
      load(tab); refreshCount();
    } catch { toast.error("Could not remove"); }
  };

  const refreshAll = () => { load(tab); refreshCount(); api.get("/reverse-pickups?tab=pending").then((r) => setPending(Array.isArray(r.data) ? r.data : [])).catch(() => {}); };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="reverse-pickup-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl bg-[#0B3D3D] text-[#C5A253] flex items-center justify-center"><Undo2 className="h-5 w-5" /></span>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-[#111411] tracking-tight">Reverse Pickup</h1>
            <p className="text-sm text-muted-foreground">Reverse pickups awaiting dispatch details, and those already created or scheduled</p>
          </div>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="rvp-add-button" className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
          <Plus className="h-4 w-4" /> Add reverse pickup
        </Button>
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        <button data-testid="rvp-tab-pending" onClick={() => setTab("pending")}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "pending" ? "border-[#0B3D3D] text-[#0B3D3D]" : "border-transparent text-muted-foreground hover:text-[#111411]"}`}>
          Pending Data {pending.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#C24127] text-white text-[10px] font-bold align-middle">{pending.length}</span>}
        </button>
        <button data-testid="rvp-tab-scheduled" onClick={() => setTab("scheduled")}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "scheduled" ? "border-[#0B3D3D] text-[#0B3D3D]" : "border-transparent text-muted-foreground hover:text-[#111411]"}`}>
          Scheduled
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
      ) : tab === "pending" ? (
        <div className="rounded-lg border border-border bg-white overflow-hidden">
          {pending.length === 0 ? (
            <div className="p-16 text-center" data-testid="rvp-pending-empty">
              <Undo2 className="h-10 w-10 mx-auto text-[#C5A253] mb-3" />
              <p className="font-heading font-bold text-[#111411]">All caught up</p>
              <p className="text-sm text-muted-foreground mt-1">No reverse pickups are awaiting dispatch details right now.</p>
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-border">
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input data-testid="rvp-pending-search" value={pendingSearch} onChange={(e) => setPendingSearch(e.target.value)}
                         placeholder="Search order, city, reason…" className="pl-9 h-9" />
                </div>
              </div>
              <div className="divide-y divide-border">
                {pendingRows.length === 0 ? (
                  <div className="p-10 text-center text-sm text-muted-foreground" data-testid="rvp-pending-filtered-empty">No matches for “{pendingSearch}”.</div>
                ) : pendingRows.map((p) => (
                <div key={p.id} data-testid={`rvp-pending-${p.id}`}
                     className="flex flex-wrap items-center gap-3 p-4 hover:bg-[#FAFAF7] transition-colors">
                  <div className="flex items-center gap-2 text-[#B45309]"><Undo2 className="h-4 w-4" /></div>
                  <div className="min-w-0">
                    <Link to={`/shipments/${p.id}`} className="font-semibold text-[#111411] hover:text-[#0B3D3D]">#{p.order_id}</Link>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {[p.city, p.state].filter(Boolean).join(", ") || "—"}
                      {p.rvp_reason ? ` · ${p.rvp_reason}` : ""}
                    </p>
                  </div>
                  <Button size="sm" data-testid={`rvp-fill-${p.id}`} onClick={() => setFlagFor(p)}
                          className="ml-auto gap-1.5 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
                    Add details <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="rvp-search" value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search order, RVP ID, AWB, reason…" className="pl-9 h-9" />
          </div>
          <div className="rounded-lg border border-border bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]">
              <thead>
                <tr className="bg-[#F4F6F4] text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3">Original Order ID</th>
                  <th className="px-4 py-3">Reverse Pickup ID</th>
                  <th className="px-4 py-3">RVP AWB / tracking</th>
                  <th className="px-4 py-3">RVP courier</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Destination</th>
                  <th className="px-4 py-3">Created date</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-16">
                    <Undo2 className="h-10 w-10 mx-auto text-[#C5A253] mb-3" />
                    <p className="font-heading font-bold text-[#111411]">No scheduled reverse pickups</p>
                    <p className="text-sm text-muted-foreground mt-1">Reverse pickups with full details appear here.</p>
                  </td></tr>
                ) : rows.map((s) => (
                  <tr key={s.id} data-testid={`rvp-row-${s.id}`} className="border-t border-border hover:bg-[#FAFAF7]">
                    <td className="px-4 py-3 font-medium">
                      <Link to={`/shipments/${s.id}`} data-testid={`rvp-order-link-${s.id}`} className="text-[#0B3D3D] hover:underline">{s.order_id}</Link>
                    </td>
                    <td className="px-4 py-3 font-medium">{s.rvp_order_id || "—"}</td>
                    <td className="px-4 py-3">{s.rvp_tracking_number || "—"}</td>
                    <td className="px-4 py-3">{s.rvp_courier_partner || "—"}</td>
                    <td className="px-4 py-3 max-w-[200px]"><span className="line-clamp-2 text-[#333]">{s.rvp_reason || "—"}</span></td>
                    <td className="px-4 py-3 text-sm">{[s.city, s.state].filter(Boolean).join(", ") || "—"}</td>
                    <td className="px-4 py-3">{fmtDate(s.reverse_pickup_date)}</td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`rvp-menu-${s.id}`}><MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem data-testid={`rvp-edit-${s.id}`} onClick={() => setFlagFor(s)}>
                            <Undo2 className="h-4 w-4 mr-2" /> Edit RVP details
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`rvp-open-${s.id}`} onClick={() => navigate(`/shipments/${s.id}`)}>
                            <ArrowRight className="h-4 w-4 mr-2" /> Open shipment
                          </DropdownMenuItem>
                          {s.tracking_link && (
                            <DropdownMenuItem data-testid={`rvp-track-${s.id}`} onClick={() => window.open(s.tracking_link, "_blank")}>
                              <ExternalLink className="h-4 w-4 mr-2" /> Track
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem data-testid={`rvp-remove-${s.id}`} onClick={() => removeFlag(s)} className="text-[#C24127]">
                            <X className="h-4 w-4 mr-2" /> Remove from reverse pickup
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ReversePickupDialog open={addOpen} onOpenChange={setAddOpen} existing={null} onDone={refreshAll} />
      <ReversePickupDialog open={!!flagFor} onOpenChange={(o) => !o && setFlagFor(null)} existing={flagFor} onDone={refreshAll} />
    </div>
  );
}
